import { compile } from './compile.js';
/**
 * Language registry.
 *
 * Registering is cheap — grammars are compiled on first use, not on import —
 * so a bundle that registers ten languages and highlights one only pays for
 * the one.
 */
const sources = new Map();
const cache = new Map();
export const registerLanguage = (...grammars) => {
    for (const grammar of grammars) {
        const name = grammar.name.toLowerCase();
        const keys = [name, ...(grammar.aliases ?? [])].map((key) => key.toLowerCase());
        // Registering the same grammar again changes nothing, so it keeps its
        // compiled form. Evicting it here made a caller that registers on every
        // render — a component's setup, say — recompile on every call, which is
        // close to twenty times the cost of the highlight itself.
        if (keys.every((key) => sources.get(key) === grammar)) {
            continue;
        }
        // Re-registering replaces a language outright, so retire every name the
        // previous grammar answered to before claiming the new one's. Evicting only
        // the canonical name would leave each alias pointing at, and cached as, the
        // grammar this call is replacing.
        const previous = sources.get(name);
        if (previous) {
            for (const key of [previous.name, ...(previous.aliases ?? [])]) {
                const lower = key.toLowerCase();
                if (sources.get(lower) === previous) {
                    sources.delete(lower);
                }
                cache.delete(lower);
            }
        }
        for (const key of keys) {
            sources.set(key, grammar);
            cache.delete(key);
        }
    }
};
export const isRegistered = (name) => {
    return sources.has(name.toLowerCase());
};
/** Canonical names of registered languages, without aliases. */
export const listLanguages = () => {
    return [...new Set([...sources.values()].map((g) => g.name))].sort();
};
const isCompiled = (value) => {
    return 'root' in value;
};
/** Looks up and compiles a language, memoizing the compiled form. */
export const getLanguage = (name) => {
    const key = name.toLowerCase();
    const hit = cache.get(key);
    if (hit) {
        return hit;
    }
    const grammar = sources.get(key);
    if (!grammar) {
        return undefined;
    }
    const built = compile(grammar);
    // Cache under every alias so repeat lookups skip the alias hop.
    for (const alias of [grammar.name, ...(grammar.aliases ?? [])]) {
        cache.set(alias.toLowerCase(), built);
    }
    return built;
};
/**
 * Accepts a registered language name, a grammar, or an already-compiled
 * grammar. Throws on an unknown name — silently rendering unhighlighted code
 * hides typos in a `lang` prop for a long time.
 */
export const resolveGrammar = (lang) => {
    if (typeof lang !== 'string') {
        return isCompiled(lang) ? lang : compile(lang);
    }
    const found = getLanguage(lang);
    if (found) {
        return found;
    }
    const known = listLanguages();
    throw new Error(`Language "${lang}" is not registered. ` +
        (known.length
            ? `Registered: ${known.join(', ')}.`
            : `Import it and call registerLanguage(), or use loadLanguage() from "@scalar/highlight/lazy".`));
};
